import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { FileText, Plus, Download } from "lucide-react";

const STATUS_BADGE = {
  draft: "bg-gray-100 text-gray-700",
  preview_ready: "bg-amber-100 text-amber-700",
  paid: "bg-blue-100 text-blue-700",
  sent_for_signature: "bg-blue-100 text-blue-700",
  seller_signed: "bg-blue-100 text-blue-700",
  buyer_signed: "bg-blue-100 text-blue-700",
  completed: "bg-[var(--csd-primary)] text-white",
  cancelled: "bg-red-100 text-red-700",
};

export default function Dashboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listAgreements().then((r) => setRows(r)).finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <SEO title="Dashboard | CarSaleDoc" description="Your bills of sale." path="/dashboard" noindex />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Your bills of sale</h1>
            <p className="text-[var(--csd-text-secondary)] mt-2 text-sm">Drafts and completed agreements tied to this device.</p>
          </div>
          <Link to="/builder/new" data-testid="dashboard-create-cta">
            <Button className="csd-btn-primary rounded-xl"><Plus size={16} className="mr-2" />New bill of sale</Button>
          </Link>
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-[var(--csd-border)] overflow-hidden">
          <div className="grid grid-cols-12 bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500 py-4 px-6">
            <div className="col-span-4">Vehicle</div>
            <div className="col-span-2">State</div>
            <div className="col-span-2">Buyer / Seller</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {loading && <div className="p-8 text-sm text-[var(--csd-text-secondary)]">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-12 text-center" data-testid="dashboard-empty">
              <FileText size={28} className="text-[var(--csd-primary)] mx-auto mb-3" />
              <div className="font-semibold">No bills of sale yet.</div>
              <div className="text-sm text-[var(--csd-text-secondary)] mt-1 mb-4">Start your first one — it's free to create.</div>
              <Link to="/builder/new"><Button className="csd-btn-primary rounded-xl">Create Bill of Sale</Button></Link>
            </div>
          )}
          {rows.map((r) => (
            <div key={r.id} className="grid grid-cols-12 px-6 py-4 border-t border-gray-200 hover:bg-gray-50 items-center" data-testid={`dashboard-row-${r.id}`}>
              <div className="col-span-4">
                <Link to={`/builder/${r.id}`} className="font-semibold hover:text-[var(--csd-primary)]">
                  {r.vehicle_label || "Untitled draft"}
                </Link>
                <div className="text-xs text-[var(--csd-text-secondary)]">Created {new Date(r.created_at).toLocaleDateString()}</div>
              </div>
              <div className="col-span-2 text-sm">{r.state || "—"}</div>
              <div className="col-span-2 text-sm">
                <div className="truncate">{r.buyer_name || "—"}</div>
                <div className="truncate text-xs text-[var(--csd-text-secondary)]">{r.seller_name || "—"}</div>
              </div>
              <div className="col-span-2">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_BADGE[r.status] || "bg-gray-100 text-gray-700"}`}>
                  {r.status.replace(/_/g, " ")}
                </span>
              </div>
              <div className="col-span-2 text-right space-x-2">
                <a href={api.pdfUrl(r.id)} target="_blank" rel="noreferrer" data-testid={`dashboard-pdf-${r.id}`}>
                  <Button size="sm" variant="outline"><Download size={14} className="mr-1" />PDF</Button>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
